"use client";

import { postData } from "@/api/api";
import { useState } from "react";
import { Mail, Send, CheckCircle, Gift } from "lucide-react";

type IProps = {
  style_2?: boolean;
  style_3?: boolean;
};

const SubscribeArea = ({ style_2, style_3 }: IProps) => {
  const [toastMessage, setToastMessage] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!email) {
        setToastMessage("Please enter email");
        setSuccess(false);
        return;
      }

      const resp = await postData(`/newsletters`, { email });

      if (!resp.success) {
        console.log(resp?.message);
        setToastMessage(resp?.message);
        setSuccess(false);
        return;
      }
      setToastMessage(resp?.message);
      setSuccess(true);
    } catch (error) {
      console.log(error as string);
      setToastMessage(error as string);
      setSuccess(false);
    } finally {
      setLoading(false);
      setEmail("");
      setTimeout(() => {
        setToastMessage("");
        setSuccess(false);
      }, 3000);
    }
  };

  return (
    <section className="subscribe-section" style={{
      padding: style_2 ? "100px 0" : "80px 0",
      background: "linear-gradient(135deg, #FFF9F5 0%, #FFFBF8 50%, #FFF5EE 100%)",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Decorative Background Elements */}
      <div className="bg-circle-1" style={{
        position: "absolute",
        top: "-10%",
        left: "-5%",
        width: "400px",
        height: "400px",
        background: "radial-gradient(circle, rgba(255,182,193,0.15) 0%, transparent 70%)",
        borderRadius: "50%",
        pointerEvents: "none",
        zIndex: 0,
      }}></div>

      <div className="bg-circle-2" style={{
        position: "absolute",
        bottom: "-15%",
        right: "-5%",
        width: "350px",
        height: "350px",
        background: "radial-gradient(circle, rgba(255,215,180,0.12) 0%, transparent 70%)",
        borderRadius: "50%",
        pointerEvents: "none",
        zIndex: 0,
      }}></div>

      {/* Floating gift emojis */}
      <div className="gift-emoji-1" style={{
        position: "absolute",
        top: "15%",
        right: "10%",
        fontSize: "48px",
        opacity: 0.1,
        transform: "rotate(15deg)",
        pointerEvents: "none",
        zIndex: 0,
      }}>🎁</div>

      <div className="gift-emoji-2" style={{
        position: "absolute",
        bottom: "20%",
        left: "8%",
        fontSize: "56px",
        opacity: 0.08,
        transform: "rotate(-20deg)",
        pointerEvents: "none",
        zIndex: 0,
      }}>🎀</div>

      <div className="subscribe-container" style={{
        maxWidth: "900px",
        margin: "0 auto",
        paddingLeft: "24px",
        paddingRight: "24px",
        position: "relative",
        zIndex: 10,
      }}>
        {/* Main Content Card */}
        <div className="subscribe-card" style={{
          background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
          borderRadius: "28px",
          padding: "60px 40px",
          boxShadow: `
            0 4px 12px rgba(255,107,107,0.3),
            0 8px 24px rgba(255,107,107,0.2),
            0 16px 48px rgba(255,107,107,0.15)
          `,
          border: "2px solid rgba(255,255,255,0.2)",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Glow background */}
          <div style={{
            position: "absolute",
            top: "-50%",
            right: "-10%",
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)",
            borderRadius: "50%",
            filter: "blur(80px)",
            pointerEvents: "none",
            animation: "pulse 4s ease-in-out infinite",
          }}></div>

          {/* Content */}
          <div style={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
          }}>
            {/* Header Section */}
            <div style={{ marginBottom: "32px" }}>
              <div style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "16px",
                padding: "10px 20px",
                backgroundColor: "rgba(255,255,255,0.25)",
                borderRadius: "50px",
                border: "2px solid rgba(255,255,255,0.3)",
                backdropFilter: "blur(10px)",
              }}>
                <Gift style={{ width: "18px", height: "18px", color: "#fff" }} />
                <span className="badge-text" style={{
                  fontSize: "13px",
                  fontWeight: 800,
                  color: "#ffffff",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}>
                  Gift Newsletter
                </span>
              </div>

              <h2 className="subscribe-title" style={{
                fontSize: "48px",
                fontWeight: 900,
                color: "#ffffff",
                margin: "0 0 16px 0",
                letterSpacing: "-1px",
                lineHeight: 1.2,
                textShadow: "0 2px 20px rgba(0,0,0,0.1)",
              }}>
                Never Miss a Special Gift 🎁
              </h2>

              <p className="subscribe-description" style={{
                fontSize: "18px",
                fontWeight: 500,
                color: "rgba(255,255,255,0.95)",
                margin: 0,
                lineHeight: 1.7,
                maxWidth: "600px",
                marginLeft: "auto",
                marginRight: "auto",
              }}>
                Subscribe for exclusive gift ideas, seasonal collections, and special offers delivered to your inbox
              </p>
            </div>

            {/* Form Section */}
            <form onSubmit={handleSubmit} className="subscribe-form" style={{
              display: "flex",
              gap: "12px",
              marginBottom: "20px",
              maxWidth: "550px",
              margin: "0 auto 20px auto",
            }}>
              <div className="input-wrapper" style={{
                flex: 1,
                position: "relative",
                display: "flex",
                alignItems: "center",
              }}>
                <Mail className="mail-icon" style={{
                  position: "absolute",
                  left: "18px",
                  width: "20px",
                  height: "20px",
                  color: "#FF6B6B",
                  pointerEvents: "none",
                }} />
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="email-input"
                  style={{
                    width: "100%",
                    padding: "16px 18px 16px 52px",
                    borderRadius: "50px",
                    border: "2px solid rgba(255,255,255,0.3)",
                    backgroundColor: "rgba(255,255,255,0.95)",
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#2C2C2C",
                    outline: "none",
                    transition: "all 0.3s ease",
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)";
                    e.currentTarget.style.boxShadow = "0 0 0 4px rgba(255,255,255,0.2)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.95)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="subscribe-button"
                style={{
                  padding: "16px 38px",
                  borderRadius: "50px",
                  border: "2px solid rgba(255,255,255,0.3)",
                  backgroundColor: "#ffffff",
                  color: "#FF6B6B",
                  fontSize: "15px",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  opacity: loading ? 0.7 : 1,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = "#2C2C2C";
                    e.currentTarget.style.color = "#ffffff";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                    e.currentTarget.style.color = "#FF6B6B";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.1)";
                  }
                }}
              >
                {loading ? (
                  <>
                    <span style={{ animation: "spin 1s linear infinite" }}>⏳</span>
                    <span className="button-text">Subscribing...</span>
                  </>
                ) : (
                  <>
                    <Send style={{ width: "18px", height: "18px" }} />
                    <span className="button-text">Subscribe</span>
                  </>
                )}
              </button>
            </form>

            {/* Toast Message */}
            {toastMessage && (
              <div className="toast-message" style={{
                padding: "16px 20px",
                borderRadius: "50px",
                border: "2px solid",
                fontSize: "14px",
                fontWeight: 700,
                animation: "slideDown 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                marginTop: "16px",
                backgroundColor: success ? "#DCFCE7" : "#FEE2E2",
                borderColor: success ? "#BBF7D0" : "#FECACA",
                color: success ? "#166534" : "#DC2626",
                maxWidth: "550px",
                margin: "16px auto 0 auto",
              }}>
                {success ? (
                  <CheckCircle style={{ width: "20px", height: "20px" }} />
                ) : (
                  <span>⚠️</span>
                )}
                {toastMessage}
              </div>
            )}

            {/* Benefit Text */}
            <p className="benefit-text" style={{
              fontSize: "14px",
              fontWeight: 600,
              color: "rgba(255,255,255,0.9)",
              margin: "24px 0 0 0",
              letterSpacing: "0.3px",
            }}>
              🎁 Gift Ideas · ✨ Seasonal Collections · 💝 Exclusive Offers · 🎀 Early Access
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.15;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.25;
          }
        }

        /* TABLET & MOBILE RESPONSIVE */
        @media (max-width: 768px) {
          .subscribe-section {
            padding: 60px 0 !important;
          }

          .bg-circle-1, .bg-circle-2 {
            width: 250px !important;
            height: 250px !important;
          }

          .gift-emoji-1, .gift-emoji-2 {
            display: none !important;
          }

          .subscribe-container {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }

          .subscribe-card {
            padding: 40px 24px !important;
            border-radius: 24px !important;
          }

          .badge-text {
            font-size: 11px !important;
          }

          .subscribe-title {
            font-size: 32px !important;
            margin-bottom: 12px !important;
          }

          .subscribe-description {
            font-size: 16px !important;
            line-height: 1.6 !important;
          }

          .subscribe-form {
            flex-direction: column !important;
            gap: 12px !important;
            max-width: 100% !important;
          }

          .input-wrapper {
            width: 100% !important;
          }

          .mail-icon {
            left: 16px !important;
            width: 18px !important;
            height: 18px !important;
          }

          .email-input {
            font-size: 14px !important;
            padding: 14px 16px 14px 48px !important;
          }

          .subscribe-button {
            width: 100% !important;
            padding: 14px 32px !important;
            font-size: 14px !important;
          }

          .button-text {
            font-size: 14px !important;
          }

          .toast-message {
            font-size: 13px !important;
            padding: 14px 18px !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .benefit-text {
            font-size: 12px !important;
            line-height: 1.7 !important;
          }
        }

        /* SMALL MOBILE */
        @media (max-width: 480px) {
          .subscribe-card {
            padding: 32px 20px !important;
            border-radius: 20px !important;
          }

          .subscribe-title {
            font-size: 26px !important;
            letter-spacing: -0.5px !important;
          }

          .subscribe-description {
            font-size: 15px !important;
          }

          .email-input {
            font-size: 13px !important;
            padding: 13px 14px 13px 46px !important;
          }

          .subscribe-button {
            padding: 13px 28px !important;
            font-size: 13px !important;
          }

          .benefit-text {
            font-size: 11px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default SubscribeArea;